import {defineSlotRecipe} from '@chakra-ui/react3'

const chakraCardSlotRecipe = defineSlotRecipe({
    className: 'chakra-card',
    slots: ['root', 'header', 'body', 'footer', 'title', 'description'],
    base: {
        root: {
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            minWidth: '0',
            wordWrap: 'break-word',
            borderRadius: 'l3',
            color: 'fg',
            textAlign: 'start'
        },
        title: {
            fontWeight: 'semibold'
        },
        description: {
            color: 'fg.muted',
            fontSize: 'sm'
        },
        header: {
            paddingInline: 'var(--card-padding)',
            paddingTop: 'var(--card-padding)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5'
        },
        body: {
            padding: 'var(--card-padding)',
            flex: '1',
            display: 'flex',
            flexDirection: 'column'
        },
        footer: {
            display: 'flex',
            alignItems: 'center',
            gap: '2',
            paddingInline: 'var(--card-padding)',
            paddingBottom: 'var(--card-padding)'
        }
    },
    variants: {
        size: {
            sm: {
                root: {
                    '--card-padding': 'spacing.4'
                },
                title: {
                    textStyle: 'md'
                }
            },
            md: {
                root: {
                    '--card-padding': 'spacing.6'
                },
                title: {
                    textStyle: 'lg'
                }
            },
            lg: {
                root: {
                    '--card-padding': 'spacing.7'
                },
                title: {
                    textStyle: 'xl'
                }
            }
        },
        variant: {
            elevated: {
                root: {
                    bg: 'bg.panel',
                    boxShadow: 'md'
                }
            },
            outline: {
                root: {
                    bg: 'bg.panel',
                    borderWidth: '1px',
                    borderColor: 'border'
                }
            },
            subtle: {
                root: {
                    bg: 'bg.muted'
                }
            }
        }
    },
    defaultVariants: {
        variant: 'outline',
        size: 'md'
    }
})

export const cardSlotRecipe = defineSlotRecipe({
    ...chakraCardSlotRecipe,
    base: {
        ...chakraCardSlotRecipe.base,
        title: {},
        description: {}
    },
    variants: {
        ...chakraCardSlotRecipe.variants,
        variant: {
            ...chakraCardSlotRecipe.variants.variant,
            'xpdp-configurator': {
                root: {
                    flexDirection: 'row',
                    alignItems: 'center',
                    border: 'sm',
                    borderRadius: 'm',
                    borderColor: 'border.subtle',
                    gap: {base: 'spacing-16', xs: 'spacing-20'}, // includes 4px padding on the image
                    padding: 'spacing-4' // the image padding
                },
                title: {
                    color: 'text.primary',
                    textStyle: 'heading.medium'
                }
            }
        },
        'xpdp-configurator-step': {
            colors: {
                root: {
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    borderColor: 'border.medium',
                    gap: 'spacing-24',
                    px: 'spacing-4',
                    pt: 'spacing-4',
                    pb: 'spacing-24'
                },
                header: {
                    flexDirection: 'row',
                    backgroundColor: {
                        base: 'background.section.default',
                        s: 'background.section.strong'
                    },
                    borderRadius: {
                        base: '{radii.s} {radii.s} 0 0',
                        s: 's'
                    },
                    paddingInline: 'spacing-16',
                    gap: 'spacing-16'
                },
                footer: {
                    px: 'spacing-20'
                },
                description: {
                    display: 'flex',
                    gap: 'spacing-4',
                    color: 'text.tertiary',
                    textStyle: 'heading.small'
                }
            }
        },
        'xpdp-configurator': {
            'line-item': {
                root: {
                    flexDirection: 'row',
                    border: 'sm',
                    borderRadius: 'm',
                    borderColor: 'border.subtle',
                    alignItems: {base: 'stretch', s: 'center'},
                    padding: '0',
                    width: '100%',
                    height: {s: '8.125rem'},
                    maxHeight: {s: '8.125rem'},
                    minHeight: {base: '6.625rem', s: '8rem'},
                    gap: {base: 'spacing-12', xs: 'spacing-12', s: 'spacing-16'},
                    _pressed: {borderColor: 'border.brand'},
                    _hover: {borderColor: 'primary.base.black'},
                    _focusVisible: {borderColor: 'primary.base.black', outline: 'none'}
                },
                body: {
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: {base: 'spacing-4', s: 'spacing-8'},
                    paddingTop: {base: 'spacing-12', s: 'spacing-16'},
                    paddingRight: {base: 'spacing-12', s: 'spacing-16'}
                },
                title: {
                    color: 'text.primary',
                    textStyle: {base: 'heading.small', s: 'heading.medium'}
                },
                description: {
                    color: 'text.secondary',
                    textStyle: 'body.xsmall'
                },
                footer: {
                    display: 'flex',
                    minWidth: '100%',
                    justifyContent: 'space-between',
                    paddingLeft: '0',
                    paddingBottom: {base: 'spacing-12', s: 'spacing-16'},
                    paddingRight: {base: 'spacing-12', s: 'spacing-16'}
                }
            }
        }
    }
})
